import React, { useState, useEffect } from 'react';
import type { PlanStep, EmailContent, SMSContent, TrackerComContent, StepContent } from '../../types/agent.types';
import { apiClient } from '@/core/api/client';

const TEAM_MEMBERS = [
  { name: 'Frédéric Chatelain', email: 'frederic.chatelain@chatelainxpert.be' },
  { name: 'Caroline Degrange', email: 'caroline.degrange@chatelainxpert.be' },
  { name: 'Florine Lambert', email: 'florine.lambert@chatelainxpert.be' },
  { name: 'Laura Farrauto', email: 'laura.farrauto@chatelainxpert.be' },
  { name: 'Florine Andre', email: 'florine.andre@chatelainxpert.be' },
  { name: 'Coralie Dedobbeleer', email: 'coralie.dedobbeleer@chatelainxpert.be' },
  { name: 'Manon Degeye', email: 'manon.degeye@chatelainxpert.be' },
  { name: 'Morgane Metzmacher', email: 'morgane.metzmacher@chatelainxpert.be' },
  { name: 'Alexandre Odekerken', email: 'alexandre.odekerken@chatelainxpert.be' },
  { name: 'Mathilde Bertrand', email: 'mathilde.bertrand@chatelainxpert.be' },
  { name: 'Charlotte Praillet', email: 'charlotte.praillet@chatelainxpert.be' },
  { name: 'Clément Danze', email: 'stagiaire@chatelainxpert.be' },
];

function matchPerson(memberName: string, person?: string): boolean {
  if (!person) return false;
  const mParts = memberName.toLowerCase().split(' ');
  const pParts = person.toLowerCase().trim().split(/\s+/);
  const mLast = mParts[mParts.length - 1] || '';
  const pLast = pParts[pParts.length - 1] || '';
  if (memberName.toLowerCase() === person.toLowerCase().trim()) return true;
  if (mLast.length >= 3 && mLast === pLast) return true;
  return false;
}

function sortTeamByRoles(members: typeof TEAM_MEMBERS, roles?: { rv?: string; in?: string; eval1?: string; eval2?: string; eval3?: string }) {
  if (!roles) return members;
  return [...members].sort((a, b) => {
    const aScore = matchPerson(a.name, roles.rv) ? 0 : matchPerson(a.name, roles.in) ? 1 : 2;
    const bScore = matchPerson(b.name, roles.rv) ? 0 : matchPerson(b.name, roles.in) ? 1 : 2;
    return aScore - bScore;
  });
}

interface StepModifierProps {
  step: PlanStep;
  onSave: (modified: StepContent) => void;
  onCancel: () => void;
  affaireRoles?: { rv?: string; in?: string; eval1?: string; eval2?: string; eval3?: string };
  affaireSnapshot?: Record<string, unknown>;
}

export const StepModifier: React.FC<StepModifierProps> = ({ step, onSave, onCancel, affaireRoles, affaireSnapshot }) => {
  const initialContent = step.modified_content || step.content;
  const [content, setContent] = useState<StepContent>({ ...initialContent });

  // If email has template_id, resolve it to get actual body/subject for editing
  useEffect(() => {
    const email = initialContent as EmailContent;
    if (step.type === 'email' && email?.template_id && !email.body) {
      apiClient.post('/api/admin/agent/templates/resolve', {
        template_id: email.template_id,
        variables: email.variables || {},
        language: email.language || (affaireSnapshot as Record<string, string>)?.client_language || 'FR',
        snapshot: affaireSnapshot || {},
      }).then((res: any) => {
        const resolved = res?.success ? res.data : res?.data?.success ? res.data.data : null;
        if (resolved) {
          // Convert to free-form: replace template fields with resolved text
          setContent(prev => ({
            ...prev,
            subject: (prev as EmailContent).subject || resolved.subject,
            body: resolved.body,
            template_id: undefined, // Remove template — now free-form
            variables: undefined,
          } as StepContent));
        }
      }).catch(() => {});
    }
  }, []);

  const handleFieldChange = (field: string, value: string) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  if (step.type === 'email') {
    const email = content as EmailContent;
    return (
      <div className="agent-step-modifier">
        <div className="agent-modifier-title">Modifier l'email</div>
        <div className="agent-modifier-field">
          <label>À:</label>
          <input
            type="text"
            value={email.to}
            onChange={(e) => handleFieldChange('to', e.target.value)}
          />
        </div>
        <div className="agent-modifier-field">
          <label>CC:</label>
          <input
            type="text"
            value={email.cc || ''}
            onChange={(e) => handleFieldChange('cc', e.target.value)}
          />
        </div>
        <div className="agent-modifier-field">
          <label>Objet:</label>
          <input
            type="text"
            value={email.subject}
            onChange={(e) => handleFieldChange('subject', e.target.value)}
          />
        </div>
        <div className="agent-modifier-field">
          <label>Corps:</label>
          <textarea
            rows={8}
            value={email.body}
            onChange={(e) => handleFieldChange('body', e.target.value)}
          />
        </div>
        <div className="agent-modifier-actions">
          <button className="agent-modifier-btn-cancel" onClick={onCancel}>Annuler</button>
          <button className="agent-modifier-btn-save" onClick={() => onSave(content)}>
            Valider la modification
          </button>
        </div>
      </div>
    );
  }

  if (step.type === 'sms') {
    const sms = content as SMSContent;
    return (
      <div className="agent-step-modifier">
        <div className="agent-modifier-title">Modifier le SMS</div>
        <div className="agent-modifier-field">
          <label>À:</label>
          <input
            type="text"
            value={sms.to}
            onChange={(e) => handleFieldChange('to', e.target.value)}
          />
        </div>
        <div className="agent-modifier-field">
          <label>Message:</label>
          <textarea
            rows={4}
            value={sms.message}
            onChange={(e) => handleFieldChange('message', e.target.value)}
          />
        </div>
        <div className="agent-modifier-actions">
          <button className="agent-modifier-btn-cancel" onClick={onCancel}>Annuler</button>
          <button className="agent-modifier-btn-save" onClick={() => onSave(content)}>
            Valider la modification
          </button>
        </div>
      </div>
    );
  }

  if (step.type === 'tracker_com') {
    const tracker = content as TrackerComContent;
    return (
      <div className="agent-step-modifier">
        <div className="agent-modifier-title">Modifier la communication</div>
        <div className="agent-modifier-field">
          <label>Assigné à:</label>
          <select
            className="agent-assign-select"
            value={tracker.assign_to}
            onChange={(e) => handleFieldChange('assign_to', e.target.value)}
          >
            {!tracker.assign_to && <option value="">-- Choisir --</option>}
            {!TEAM_MEMBERS.some(m => m.email === tracker.assign_to) && tracker.assign_to && (
              <option value={tracker.assign_to}>{tracker.assign_to}</option>
            )}
            {sortTeamByRoles(TEAM_MEMBERS, affaireRoles).map(m => {
              const roles: string[] = [];
              if (matchPerson(m.name, affaireRoles?.rv)) roles.push('RV');
              if (matchPerson(m.name, affaireRoles?.in)) roles.push('IN');
              if (matchPerson(m.name, affaireRoles?.eval1)) roles.push('Eval 1');
              if (matchPerson(m.name, affaireRoles?.eval2)) roles.push('Eval 2');
              if (matchPerson(m.name, affaireRoles?.eval3)) roles.push('Eval 3');
              const tag = roles.length > 0 ? `  ★ ${roles.join(', ')}` : '';
              return <option key={m.email} value={m.email}>{m.name}{tag}</option>;
            })}
          </select>
        </div>
        <div className="agent-modifier-field">
          <label>Message:</label>
          <textarea
            rows={4}
            value={tracker.message}
            onChange={(e) => handleFieldChange('message', e.target.value)}
          />
        </div>
        <div className="agent-modifier-actions">
          <button className="agent-modifier-btn-cancel" onClick={onCancel}>Annuler</button>
          <button className="agent-modifier-btn-save" onClick={() => onSave(content)}>
            Valider la modification
          </button>
        </div>
      </div>
    );
  }

  return null;
};
